import * as Y from 'yjs'
import type { Node, Edge } from '@xyflow/react'

/**
 * Yjs diagram document model.
 *
 * The collaborative document holds two top-level Y.Maps:
 *   - "nodes": keyed by node id, value = the React Flow Node object
 *   - "edges": keyed by edge id, value = the React Flow Edge object
 *
 * Keying by element id (rather than storing arrays) is what makes concurrent
 * edits merge cleanly: two peers adding different elements touch different keys
 * and never clobber each other.
 *
 * These are ADDITIVE substrate helpers. They are intentionally NOT wired into
 * the live editor render path; runtime integration will consume them later.
 */

/** Transaction origins, so observers can tell who authored a change. */
export const ORIGINS = {
  agent: 'agent',
  user: 'user',
} as const

export type Origin = (typeof ORIGINS)[keyof typeof ORIGINS]

/** Create a fresh diagram Y.Doc with the canonical nodes/edges maps. */
export function createDiagramDoc(): Y.Doc {
  const ydoc = new Y.Doc()
  // Eagerly materialize the shared types so the structure is consistent across
  // peers from the first update exchange.
  ydoc.getMap('nodes')
  ydoc.getMap('edges')
  return ydoc
}

/** The nodes Y.Map (id -> Node). */
export function getNodesMap(ydoc: Y.Doc): Y.Map<Node> {
  return ydoc.getMap<Node>('nodes')
}

/** The edges Y.Map (id -> Edge). */
export function getEdgesMap(ydoc: Y.Doc): Y.Map<Edge> {
  return ydoc.getMap<Edge>('edges')
}

/**
 * Run `fn` inside a Yjs transaction tagged with `origin`. Observers receive the
 * origin on their transaction object so they can distinguish agent vs user (vs
 * remote) authorship.
 */
export function transact(ydoc: Y.Doc, origin: Origin, fn: () => void): void {
  ydoc.transact(fn, origin)
}

/** Write nodes into the doc, keyed by id, in a single transaction. */
export function writeNodes(ydoc: Y.Doc, nodes: Node[], origin: Origin = ORIGINS.user): void {
  const map = getNodesMap(ydoc)
  ydoc.transact(() => {
    for (const node of nodes) {
      map.set(node.id, node)
    }
  }, origin)
}

/** Write edges into the doc, keyed by id, in a single transaction. */
export function writeEdges(ydoc: Y.Doc, edges: Edge[], origin: Origin = ORIGINS.user): void {
  const map = getEdgesMap(ydoc)
  ydoc.transact(() => {
    for (const edge of edges) {
      map.set(edge.id, edge)
    }
  }, origin)
}

/** Read all nodes back out of the doc, preserving ids. */
export function readNodes(ydoc: Y.Doc): Node[] {
  return Array.from(getNodesMap(ydoc).values())
}

/** Read all edges back out of the doc, preserving ids. */
export function readEdges(ydoc: Y.Doc): Edge[] {
  return Array.from(getEdgesMap(ydoc).values())
}
